import { useCallback, useEffect } from 'react';
import api from '../utils/api';

/**
 * Email drip campaign event types for onboarding
 */
export const EMAIL_EVENTS = {
  // Onboarding wizard events
  WIZARD_STARTED: 'onboarding_wizard_started',
  WIZARD_COMPLETED: 'onboarding_wizard_completed',
  WIZARD_SKIPPED: 'onboarding_wizard_skipped',

  // Checklist events
  CHECKLIST_STARTED: 'onboarding_checklist_started',
  CHECKLIST_ITEM_COMPLETED: 'onboarding_checklist_item_completed',
  CHECKLIST_COMPLETED: 'onboarding_checklist_completed',

  // Feature discovery events
  FEATURE_FIRST_USE: 'onboarding_feature_first_use',
  FEATURE_NOT_USED: 'onboarding_feature_not_used',

  // Engagement events
  INACTIVE_3_DAYS: 'onboarding_inactive_3_days',
  INACTIVE_7_DAYS: 'onboarding_inactive_7_days',
  RETURNED_AFTER_INACTIVE: 'onboarding_returned_after_inactive',

  // Achievement events
  ACHIEVEMENT_UNLOCKED: 'onboarding_achievement_unlocked',
  MILESTONE_REACHED: 'onboarding_milestone_reached',
};

/**
 * Email template configurations
 */
export const EMAIL_TEMPLATES = {
  [EMAIL_EVENTS.WIZARD_COMPLETED]: {
    templateId: 'onboarding-complete',
    subject: 'Welcome to Uni-Fund! Here are your next steps',
    delayMinutes: 0, // Send immediately
  },
  [EMAIL_EVENTS.WIZARD_SKIPPED]: {
    templateId: 'onboarding-skipped',
    subject: "You skipped the tour - here's what you missed",
    delayMinutes: 60, // Send after 1 hour
  },
  [EMAIL_EVENTS.CHECKLIST_COMPLETED]: {
    templateId: 'checklist-complete',
    subject: "You're all set up! Explore advanced features",
    delayMinutes: 0,
  },
  [EMAIL_EVENTS.FEATURE_NOT_USED]: {
    templateId: 'feature-reminder',
    subject: "Have you tried {featureName} yet?",
    delayMinutes: 1440, // Send after 24 hours
  },
  [EMAIL_EVENTS.INACTIVE_3_DAYS]: {
    templateId: 'inactive-reminder',
    subject: "We miss you! Here's what's new in Uni-Fund",
    delayMinutes: 0,
  },
  [EMAIL_EVENTS.INACTIVE_7_DAYS]: {
    templateId: 'inactive-urgent',
    subject: "Don't miss out - your Uni-Fund account is waiting",
    delayMinutes: 0,
  },
  [EMAIL_EVENTS.ACHIEVEMENT_UNLOCKED]: {
    templateId: 'achievement',
    subject: "Congratulations! You unlocked: {achievementName}",
    delayMinutes: 0,
  },
};

/**
 * Feature usage tracking for drip campaigns
 */
const TRACKED_FEATURES = {
  global_search: {
    name: 'Global Search',
    minUses: 1,
    reminderDelay: 2, // days
  },
  transfers: {
    name: 'Budget Transfers',
    minUses: 1,
    reminderDelay: 3,
  },
  reports: {
    name: 'Analytics BI',
    minUses: 1,
    reminderDelay: 5,
  },
  saved_filters: {
    name: 'Saved Filters',
    minUses: 1,
    reminderDelay: 7,
  },
};

/**
 * useOnboardingEmails - Hook for managing email drip campaign triggers
 *
 * Tracks user activity and triggers appropriate email campaigns.
 * Works with backend email service to schedule and send emails.
 */
export function useOnboardingEmails(userId, userEmail) {
  // Track an email event
  const trackEvent = useCallback(async (eventType, metadata = {}) => {
    if (!userId) return;

    try {
      await api.post('/users/me/onboarding-email-events', {
        event: eventType,
        metadata,
        timestamp: new Date().toISOString(),
      });
    } catch (err) {
      // Silently fail - email tracking is non-critical
      if (err.response?.status !== 404) {
        console.warn('Failed to track email event:', err);
      }
    }

    // Also store locally for redundancy
    try {
      const events = JSON.parse(localStorage.getItem('email_events') || '[]');
      events.push({
        event: eventType,
        metadata,
        timestamp: new Date().toISOString(),
      });
      localStorage.setItem('email_events', JSON.stringify(events.slice(-100)));
    } catch {
      // Ignore localStorage errors
    }
  }, [userId]);

  // Track wizard events
  const trackWizardStarted = useCallback(() => {
    trackEvent(EMAIL_EVENTS.WIZARD_STARTED);
  }, [trackEvent]);

  const trackWizardCompleted = useCallback((totalSteps, timeSpent) => {
    trackEvent(EMAIL_EVENTS.WIZARD_COMPLETED, { totalSteps, timeSpent });
  }, [trackEvent]);

  const trackWizardSkipped = useCallback((atStep, totalSteps) => {
    trackEvent(EMAIL_EVENTS.WIZARD_SKIPPED, { atStep, totalSteps });
  }, [trackEvent]);

  // Track checklist events
  const trackChecklistItemCompleted = useCallback((itemId, itemsRemaining) => {
    trackEvent(EMAIL_EVENTS.CHECKLIST_ITEM_COMPLETED, { itemId, itemsRemaining });

    if (itemsRemaining === 0) {
      trackEvent(EMAIL_EVENTS.CHECKLIST_COMPLETED);
    }
  }, [trackEvent]);

  // Track feature usage
  const trackFeatureUse = useCallback((featureId) => {
    const feature = TRACKED_FEATURES[featureId];
    if (!feature) return;

    // Check if this is first use
    try {
      const usageKey = `feature_usage_${featureId}`;
      const currentCount = parseInt(localStorage.getItem(usageKey) || '0', 10);

      if (currentCount === 0) {
        trackEvent(EMAIL_EVENTS.FEATURE_FIRST_USE, {
          featureId,
          featureName: feature.name,
        });
      }

      localStorage.setItem(usageKey, (currentCount + 1).toString());
    } catch {
      // Ignore
    }
  }, [trackEvent]);

  // Track achievement
  const trackAchievement = useCallback((achievementId, achievementName, points) => {
    trackEvent(EMAIL_EVENTS.ACHIEVEMENT_UNLOCKED, {
      achievementId,
      achievementName,
      points,
    });
  }, [trackEvent]);

  // Check for inactive users (should be called periodically)
  const checkInactivity = useCallback(() => {
    try {
      const lastActivity = localStorage.getItem('last_activity_timestamp');
      if (!lastActivity) {
        localStorage.setItem('last_activity_timestamp', Date.now().toString());
        return;
      }

      const daysSinceActivity = (Date.now() - parseInt(lastActivity, 10)) / (1000 * 60 * 60 * 24);

      if (daysSinceActivity >= 7) {
        const sent7Day = localStorage.getItem('inactive_7_day_sent');
        if (!sent7Day) {
          trackEvent(EMAIL_EVENTS.INACTIVE_7_DAYS);
          localStorage.setItem('inactive_7_day_sent', 'true');
        }
      } else if (daysSinceActivity >= 3) {
        const sent3Day = localStorage.getItem('inactive_3_day_sent');
        if (!sent3Day) {
          trackEvent(EMAIL_EVENTS.INACTIVE_3_DAYS);
          localStorage.setItem('inactive_3_day_sent', 'true');
        }
      }
    } catch {
      // Ignore
    }
  }, [trackEvent]);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    try {
      const wasInactive = localStorage.getItem('inactive_3_day_sent') || localStorage.getItem('inactive_7_day_sent');

      localStorage.setItem('last_activity_timestamp', Date.now().toString());

      // Clear inactive flags
      localStorage.removeItem('inactive_3_day_sent');
      localStorage.removeItem('inactive_7_day_sent');

      // Track return if was inactive
      if (wasInactive) {
        trackEvent(EMAIL_EVENTS.RETURNED_AFTER_INACTIVE);
      }
    } catch {
      // Ignore
    }
  }, [trackEvent]);

  // Check for unused features (should be called after some time)
  const checkUnusedFeatures = useCallback(() => {
    try {
      const onboardingCompleteDate = localStorage.getItem('onboarding_completed_at');
      if (!onboardingCompleteDate) return;

      const daysSinceOnboarding = (Date.now() - new Date(onboardingCompleteDate).getTime()) / (1000 * 60 * 60 * 24);

      Object.entries(TRACKED_FEATURES).forEach(([featureId, feature]) => {
        if (daysSinceOnboarding < feature.reminderDelay) return;

        const usageKey = `feature_usage_${featureId}`;
        const reminderKey = `feature_reminder_sent_${featureId}`;
        const usage = parseInt(localStorage.getItem(usageKey) || '0', 10);
        const reminderSent = localStorage.getItem(reminderKey);

        if (usage < feature.minUses && !reminderSent) {
          trackEvent(EMAIL_EVENTS.FEATURE_NOT_USED, {
            featureId,
            featureName: feature.name,
          });
          localStorage.setItem(reminderKey, 'true');
        }
      });
    } catch {
      // Ignore
    }
  }, [trackEvent]);

  // Get email preferences
  const getEmailPreferences = useCallback(async () => {
    try {
      const response = await api.get('/users/me/email-preferences');
      return response.data;
    } catch {
      return {
        onboardingEmails: true,
        featureReminders: true,
        achievementNotifications: true,
      };
    }
  }, []);

  // Update email preferences
  const updateEmailPreferences = useCallback(async (preferences) => {
    try {
      await api.patch('/users/me/email-preferences', preferences);
    } catch (err) {
      console.error('Failed to update email preferences:', err);
    }
  }, []);

  // Initialize activity tracking
  useEffect(() => {
    updateActivity();

    // Check inactivity and unused features periodically
    const interval = setInterval(() => {
      checkInactivity();
      checkUnusedFeatures();
    }, 1000 * 60 * 60); // Check every hour

    return () => clearInterval(interval);
  }, [updateActivity, checkInactivity, checkUnusedFeatures]);

  return {
    // Generic event tracking
    trackEvent,

    // Specific event tracking
    trackWizardStarted,
    trackWizardCompleted,
    trackWizardSkipped,
    trackChecklistItemCompleted,
    trackFeatureUse,
    trackAchievement,

    // Activity tracking
    updateActivity,
    checkInactivity,
    checkUnusedFeatures,

    // Preferences
    getEmailPreferences,
    updateEmailPreferences,
  };
}

export default useOnboardingEmails;
